import React, { FC, useState } from 'react';
import { RequirementsProps } from '../../tools/context';
import { useTranslation } from '../../tools/i18n';
import { imageUrlFor } from '../../tools/sanity';
import { Picto } from '../Picto';
import { FilterLayout } from './FiltersLayout';
import { Number } from './inputs/Number';

interface TravelerProfileProps {
  step: any;
  siteConfiguration: any;
  partnerStates: any;
  agentId: string;
  setStateData: (stateData: any) => void;
  stateData: any;
  callback: (parnerState: any) => void;
  config: any;
  requirements: RequirementsProps;
  back: () => void;
}

interface ItemProps {
  label: string;
  valueSelected: string;
  list: [{ _key: string; _type: string; key: string; picto?: string; type: string; image?: any }];
  onSelect?: (key: string) => void;
  requirements?: RequirementsProps;
}

interface AgeSelectorProps {
  index: number;
  onChange: (index: number, value: string) => void;
}

const Item: FC<ItemProps> = ({ valueSelected, label, list, onSelect, requirements }) => {
  const { t } = useTranslation();

  return (
    <div className="travelerProfile__group">
      <p>{label}</p>
      <div className="travelerProfile__group__items">
        {list.map((i) => (
          <button
            className={`travelerProfile__group__item ${valueSelected === i.key ? 'selected' : ''}`}
            key={i._key}
            onClick={() => onSelect(i.key)}
          >
            {i.type === 'picto' ? (
              <Picto iconKey={i.picto} />
            ) : (
              <img src={imageUrlFor(i.image.asset, requirements).width(30).url()} alt="" />
            )}

            <p>{t(`travelPlaner.TravelerProfile.${i._type}.${i.key}`)}</p>
          </button>
        ))}
      </div>
      {}
    </div>
  );
};

const AgeSelector: FC<AgeSelectorProps> = ({ index, onChange }) => {
  const { t } = useTranslation();
  const list = Array.from({ length: 18 }, (_, index) => index);
  return (
    <div className="travelerProfile__family__children_item">
      <p>{`${t(`travelPlaner.TravelerProfile.childrenLabel`)} ${index + 1}`}</p>
      <select onChange={(e) => onChange(index, e.target.value)}>
        {list.map((i) => (
          <option key={i} value={i}>
            {i}
          </option>
        ))}
      </select>
    </div>
  );
};

export const TravelerProfile = (props: TravelerProfileProps) => {
  const { config, requirements, callback, back } = props;
  const [state, setState] = useState({
    adultCount: 1,
    childrenCount: 0,
    equipment: null as string | null,
    childrenAges: [] as string[] | null,
    havePets: false as boolean | null,
  });

  const { t } = useTranslation();

  const handleEquipmentSelect = (key: string) => {
    setState((prev) => ({ ...prev, equipment: key }));
  };

  const handleAdultCountChange = (count: (prev: number) => number) => {
    setState((prev) => ({ ...prev, adultCount: count(state.adultCount) }));
  };

  const handleChildrenCountChange = (count: (prev: number) => number) => {
    setState((prev) => {
      const newCount = count(prev.childrenCount);
      const newAges = [...(prev.childrenAges || [])];

      if (newCount > prev.childrenCount) {
        const defaultAge = props?.step?.childSegmentation?.[0] || '0';
        while (newAges.length < newCount) {
          newAges.push(defaultAge);
        }
      } else if (newCount < prev.childrenCount) {
        newAges.splice(newCount);
      }

      return {
        ...prev,
        childrenCount: newCount,
        childrenAges: newAges,
      };
    });
  };

  const handleChildrenAgesChange = (index: number, value: string) => {
    setState((prev) => {
      const newAges = [...(prev.childrenAges || [])];
      newAges[index] = value;
      return { ...prev, childrenAges: newAges };
    });
  };

  const handlePetsChange = () => {
    setState((prev) => ({ ...prev, havePets: !prev.havePets }));
  };

  return (
    <FilterLayout
      config={config}
      title={t(`travelPlaner.PlaceOfDeparture.title`)}
      requirements={requirements}
      siteConfiguration={props.siteConfiguration}
      back={back}
      callback={() => callback(state)}
      disabledNext={!state.equipment}
    >
      <div className="travelerProfile">
        <div className="travelerProfile__equipment">
          <p className="subtitle">{t('travelPlaner.TravelerProfile.equipment')}</p>
          {props?.step?.carTypeList && props?.step?.carTypeList.length > 0 ? (
            <Item
              valueSelected={state.equipment}
              label={t('travelPlaner.TravelerProfile.carTypeList')}
              list={props?.step?.carTypeList}
              onSelect={handleEquipmentSelect}
              requirements={requirements}
            />
          ) : null}
          {props?.step?.caravanTypeList && props?.step?.caravanTypeList.length > 0 ? (
            <Item
              valueSelected={state.equipment}
              label={t('travelPlaner.TravelerProfile.caravanTypeList')}
              list={props?.step?.caravanTypeList}
              onSelect={handleEquipmentSelect}
              requirements={requirements}
            />
          ) : null}
          {props?.step?.tentSizeList && props?.step?.tentSizeList.length > 0 ? (
            <Item
              valueSelected={state.equipment}
              label={t('travelPlaner.TravelerProfile.tentSizeList')}
              list={props?.step?.tentSizeList}
              onSelect={handleEquipmentSelect}
              requirements={requirements}
            />
          ) : null}

          {props?.step?.askPet ? (
            <div className="travelerProfile__family__pets">
              <input
                type="checkbox"
                onChange={handlePetsChange}
                checked={state.havePets}
                name="askPets"
                id="askPets"
              />
              <label htmlFor="askPets" className="askPets-checkbox-label">
                <p className="subtitle">{t('travelPlaner.TravelerProfile.askPets')}</p>
              </label>
            </div>
          ) : null}
        </div>
        <div className="travelerProfile__family">
          <p className="subtitle">{t('travelPlaner.TravelerProfile.familyCount')}</p>
          <div className="travelerProfile__family__count">
            <div className="travelerProfile__family__count__label">
              <p>{t('travelPlaner.TravelerProfile.adultLabel')}</p>
              <p>{t('travelPlaner.TravelerProfile.adultLabelDescription')}</p>
            </div>
            <Number
              data={state.adultCount}
              setData={handleAdultCountChange}
              label={state.adultCount.toString()}
              min={1}
              max={15}
            />
          </div>
          <div className="travelerProfile__family__count">
            <div className="travelerProfile__family__count__label">
              <p>{t('travelPlaner.TravelerProfile.childrenLabel')}</p>
              <p>{t('travelPlaner.TravelerProfile.childrenLabelDescription')}</p>
            </div>
            <Number
              data={state.childrenCount}
              setData={handleChildrenCountChange}
              label={state.childrenCount.toString()}
              min={0}
              max={15}
            />
          </div>
          {state.childrenCount > 0 ? (
            <div className="travelerProfile__family__children">
              <p className="subtitle">{t('travelPlaner.TravelerProfile.childrenAge')}</p>
              {Array.from({ length: state.childrenCount }, (_, index) => (
                <AgeSelector key={index} index={index} onChange={handleChildrenAgesChange} />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </FilterLayout>
  );
};
